import { Fragment } from "react";
import { cn } from "@/lib/utils";

export type WizardStep = {
  label: string;
};

export type WizardStepIndicatorProps = {
  steps: WizardStep[];
  /** 1-based index of the active step */
  currentStep: number;
  onStepClick?: (step: number) => void;
  isStepClickable?: (step: number) => boolean;
  /** Marks a step complete even when it is not before currentStep (e.g. finished async work on the active step). */
  isStepComplete?: (step: number) => boolean;
  className?: string;
  "data-testid"?: string;
};

function stepIsComplete(
  step: number,
  currentStep: number,
  isStepComplete?: (step: number) => boolean,
): boolean {
  if (currentStep > step) return true;
  return isStepComplete?.(step) ?? false;
}

/**
 * Numbered-circle wizard progress row (Report Assistant pattern).
 */
export function WizardStepIndicator({
  steps,
  currentStep,
  onStepClick,
  isStepClickable,
  isStepComplete,
  className,
  "data-testid": testId = "wizard-step-indicator",
}: WizardStepIndicatorProps) {
  return (
    <nav
      aria-label="Progress"
      className={cn("flex flex-wrap items-center justify-center gap-2", className)}
      data-testid={testId}
    >
      {steps.map((step, index) => {
        const stepNumber = index + 1;
        const active = currentStep === stepNumber;
        const complete = stepIsComplete(stepNumber, currentStep, isStepComplete);
        const clickable = Boolean(onStepClick && (isStepClickable?.(stepNumber) ?? false));

        const circleClass = cn(
          "flex h-8 w-8 items-center justify-center rounded-full text-sm font-semibold",
          active && "bg-primary text-primary-foreground",
          !active && complete && "bg-green-600 text-white",
          !active && !complete && "bg-muted text-muted-foreground",
        );

        const labelClass = cn("ml-2 text-sm", active && "font-semibold");

        const stepContent = (
          <>
            <div className={circleClass}>{stepNumber}</div>
            <span className={labelClass}>{step.label}</span>
          </>
        );

        return (
          <Fragment key={step.label}>
            {index > 0 && (
              <div
                className={cn(
                  "h-0.5 w-12",
                  currentStep >= stepNumber ? "bg-green-600" : "bg-muted",
                )}
                aria-hidden
              />
            )}
            {clickable ? (
              <button
                type="button"
                className="flex items-center rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                onClick={() => onStepClick?.(stepNumber)}
              >
                {stepContent}
              </button>
            ) : (
              <div className="flex items-center">{stepContent}</div>
            )}
          </Fragment>
        );
      })}
    </nav>
  );
}
